
import { useState, useEffect, useRef } from "react";
import Logo from "./Logo";
import ModernSearchBar from "./ModernSearchBar";
import ComprehensiveMenu from "./ComprehensiveMenu";
import MobileMenu from "./MobileMenu";
import UnifiedNotificationCenter from "@/components/notifications/UnifiedNotificationCenter";
import { AnimatePresence } from "framer-motion";
import SafeMotion from "@/components/motion/SafeMotion";
import MotionErrorBoundary from "@/components/motion/MotionErrorBoundary";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useResponsive } from "@/hooks/useResponsive";

// Simplified responsive header with logo, search, menu, and notifications
const ModernHeader = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileSearchVisible, setIsMobileSearchVisible] = useState(false);
  const { isMobile } = useResponsive();
  const desktopSearchRef = useRef<HTMLInputElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 10);

      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY <= 0 ? 0 : currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileSearchVisible(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isMobile) {
          setIsMobileSearchVisible(true);
        } else {
          desktopSearchRef.current?.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile]);

  useEffect(() => {
    if (isMobileSearchVisible) {
      setTimeout(() => mobileSearchRef.current?.focus(), 100);
    }
  }, [isMobileSearchVisible]);

  return (
    <MotionErrorBoundary>
      <AnimatePresence>
        {isVisible && (
          <SafeMotion
            as="header"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -80, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/80 dark:bg-background/80 backdrop-blur-xl ${
              isScrolled ? "shadow-lg border-b border-border/80" : "border-transparent"
            }`}
            style={{ WebkitBackdropFilter: "blur(16px)" }}
            fallback={
              <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/80 dark:bg-background/80 backdrop-blur-xl ${
                isScrolled ? "shadow-lg border-b border-border/80" : "border-transparent"
              }`} style={{ WebkitBackdropFilter: "blur(16px)" }}>
                <nav className="w-full px-4 md:px-8 lg:px-12 h-16 grid grid-cols-3 md:flex items-center md:justify-between md:gap-4">
                  <div className="flex justify-start">
                    <div className="md:hidden">
                      <Button variant="ghost" size="icon" onClick={() => setIsMobileSearchVisible(true)} className="rounded-full">
                        <Search className="h-5 w-5" />
                        <span className="sr-only">Search</span>
                      </Button>
                    </div>
                    <div className="hidden md:block flex-shrink-0">
                      <Logo />
                    </div>
                  </div>
                  <div className="flex justify-center md:flex-1 md:px-4">
                    <div className="md:hidden">
                      <Logo />
                    </div>
                    <div className="hidden md:flex w-full max-w-md">
                      <ModernSearchBar ref={desktopSearchRef} />
                    </div>
                  </div>
                  <div className="flex justify-end items-center gap-2">
                    <div className="relative">
                      <UnifiedNotificationCenter />
                    </div>
                    <div className="md:hidden">
                      <MobileMenu />
                    </div>
                    <div className="hidden md:flex items-center flex-shrink-0 gap-1">
                      <ComprehensiveMenu />
                    </div>
                  </div>
                </nav>
              </header>
            }
          >
            <nav className="w-full px-4 md:px-8 lg:px-12 h-16 grid grid-cols-3 md:flex items-center md:justify-between md:gap-4">
              {/* --- LEFT SLOT --- */}
              <div className="flex justify-start">
                {/* Mobile: Search Icon */}
                <div className="md:hidden">
                  <Button variant="ghost" size="icon" onClick={() => setIsMobileSearchVisible(true)} className="rounded-full">
                    <Search className="h-5 w-5" />
                    <span className="sr-only">Search</span>
                  </Button>
                </div>
                {/* Desktop: Logo */}
                <div className="hidden md:block flex-shrink-0">
                  <Logo />
                </div>
              </div>

              {/* --- CENTER SLOT --- */}
              <div className="flex justify-center md:flex-1 md:px-4">
                {/* Mobile: Logo */}
                <div className="md:hidden">
                  <Logo />
                </div>
                {/* Desktop: Search Bar */}
                <div className="hidden md:flex w-full max-w-md">
                  <ModernSearchBar ref={desktopSearchRef} />
                </div>
              </div>

              {/* --- RIGHT SLOT --- */}
              <div className="flex justify-end items-center gap-2">
                {/* Notification Center - Always visible */}
                <div className="relative">
                  <UnifiedNotificationCenter />
                </div>
                
                {/* Mobile: Menu */}
                <div className="md:hidden">
                  <MobileMenu />
                </div>
                {/* Desktop: Menu */}
                <div className="hidden md:flex items-center flex-shrink-0 gap-1">
                  <ComprehensiveMenu />
                </div>
              </div>
            </nav>
          </SafeMotion>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMobileSearchVisible && (
            <SafeMotion
                key="mobile-search"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-lg"
                fallback={
                  <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-lg">
                     <div className="w-full px-4 md:px-8 lg:px-12 h-16 flex items-center justify-center">
                      <div className="w-full relative">
                        <ModernSearchBar ref={mobileSearchRef}/>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="absolute top-3 right-3 text-muted-foreground rounded-full"
                        onClick={() => setIsMobileSearchVisible(false)}
                      >
                        <X className="h-6 w-6" />
                        <span className="sr-only">Close search</span>
                      </Button>
                    </div>
                  </div>
                }
            >
                <div className="w-full px-4 md:px-8 lg:px-12 h-16 flex items-center justify-center">
                    <SafeMotion
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -20, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="w-full relative"
                        fallback={
                          <div className="w-full relative">
                            <ModernSearchBar ref={mobileSearchRef}/>
                          </div>
                        }
                    >
                        <ModernSearchBar ref={mobileSearchRef}/>
                    </SafeMotion>
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className="absolute top-3 right-3 text-muted-foreground rounded-full"
                        onClick={() => setIsMobileSearchVisible(false)}
                    >
                        <X className="h-6 w-6" />
                        <span className="sr-only">Close search</span>
                    </Button>
                </div>
            </SafeMotion>
        )}
      </AnimatePresence>
    </MotionErrorBoundary>
  );
};

export default ModernHeader;
